import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {FileManagerActionsService, IFileManagerAction} from './fileManagerActions.service';
import {IOuterFile} from '../filesList/interface/IOuterFile';
import {empty, Observable, of} from 'rxjs';
import {IFileModel} from '../filesList/interface/IFileModel';
import {FileManagerApiService} from './fileManagerApi.service';
import {FilemanagerNotifcations} from '../services/FilemanagerNotifcations';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import {
  DeleteFileSuccessAction,
  DeleteSelectedFilesSuccessAction, DownloadFileErrorAction, DownloadFileSuccessAction,
  FileManagerAction,
  LoadFilesAction,
  LoadFilesSuccessAction,
  MoveFilesErrorAction,
  MoveFilesSuccessAction,
  UploadFilesErrorAction,
  UploadFilesSuccessAction
} from './file-manager.action';
import {TreeActionTypes, TreeMoveNodeAction} from "../../angular2-tree/store/treeActions.service";
import {IOuterNode} from "../../angular2-tree/interfaces/IOuterNode";
import { Action } from "@ngrx/store";
import { AlertService } from '../../../_services/alert.service';
import { inject } from '@angular/core/primitives/di';

@Injectable()
export class FileManagerEffectsService {
  alertService = inject(AlertService);
  public loadFiles$ = createEffect(() => this.actions$
    .pipe(
      ofType(FileManagerActionsService.FILEMANAGER_LOAD_FILES),
      switchMap((action: IFileManagerAction) => this.loadFiles(action.payload.folderId)
        .pipe(
          map((files: IOuterFile[]): FileManagerAction => {
            return new LoadFilesSuccessAction({files});
          }),
          catchError((e) => {
            return of(this.onLoadFilesError(action.payload.folderId));
          })
        )
      )
    )
  );

  public deleteFile$ = createEffect(() => this.actions$
    .pipe(
      ofType(FileManagerActionsService.FILEMANAGER_DELETE_FILE),
      switchMap((action: IFileManagerAction) => this.deleteFile(action.payload.file)
        .pipe(
          map((result: boolean): FileManagerAction => {
            return new DeleteFileSuccessAction({file: action.payload.file});
          }),
          catchError(() => of(this.onDeleteFileError(action.payload.file)))
        )
      )
    )
  );

  public downloadFile$ = createEffect(() => this.actions$
    .pipe(
      ofType(FileManagerActionsService.FILEMANAGER_DOWNLOAD_FILE),
      switchMap((action: IFileManagerAction) => this.downloadFile(action.payload.file)
        .pipe(
          map(data => {
            this.filemanagerNotfication.sendNotification({
              type: 'success',
              title: 'Download File',
              message: 'File has been downloaded.'
            });
            return new DownloadFileSuccessAction({file: action.payload.file});
          }),
          catchError(() => of(new DownloadFileErrorAction({file: action.payload.file})))
        )
      )
    )
  );

  public deleteFilesSelection$ = createEffect(() => this.actions$
    .pipe(
      ofType(FileManagerActionsService.FILEMANAGER_DELETE_FILE_SELECTION),
      switchMap((action: IFileManagerAction) => this.deleteFilesSelection(action.payload.fileIds)
        .pipe(
          map((result: boolean): FileManagerAction => {
            return new DeleteSelectedFilesSuccessAction({files: action.payload.fileIds});
          }),
          catchError(() => of(this.onDeleteFilesSelectionError(action.payload.files)))
        )
      )
    )
  );


  public uploadFile$ = createEffect(() => this.actions$
    .pipe(
      ofType(FileManagerActionsService.FILEMANAGER_UPLOAD_FILE),
      switchMap((action: IFileManagerAction) => this.uploadFile(action.payload.files[0])
        .pipe(
          map((result: IOuterFile): FileManagerAction => {
            return new UploadFilesSuccessAction({files: [result]});
          }),
          catchError(() => {
            return empty();
          })
        )
      )
    )
  );

  public moveFile$ = createEffect(() => this.actions$
    .pipe(
      ofType(TreeActionTypes.TREE_MOVE_NODE),
      filter((action: TreeMoveNodeAction) => {
        return action.payload.sourceOfDroppedData === 'FILE';
      }),
      switchMap((action: TreeMoveNodeAction) => this.moveFiles([<IOuterFile>action.payload.oldNode], action.payload.node)
        .pipe(
          map((result: IOuterFile[]): FileManagerAction => {
            const folderId = (<IOuterFile>action.payload.oldNode).folderId;

            return new MoveFilesSuccessAction({files: result, folderId});
          }),
          catchError(() => {
            return of(new MoveFilesErrorAction({files: [<IOuterFile>action.payload.oldNode]}));
          })
        )
      )
    )
  );

  public filesMoveSuccess$ = this.actions$
    .pipe(
      ofType(FileManagerActionsService.FILEMANAGER_MOVE_FILES_SUCCESS),
      map((action: MoveFilesSuccessAction) => {
        this.onMoveFilesSuccess();

        return new LoadFilesAction({folderId: action.payload.folderId});
      })
    );

  public uploadError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FileManagerActionsService.FILEMANAGER_UPLOAD_FILE_ERROR),
        tap((response: any) => {
          let msg = Array.isArray(response.payload.files)
            ? `${response.payload.files[0].name} exists on the server in this directory`
            : response.payload.files.message
          this.alertService.error(msg);
        })
      ),
    { dispatch: false }
  );
  // TODO: recover original functionality instead of using alertingService above
  //public uploadError$ = createEffect(() => this.actions$
  //  .pipe(
  //    ofType(FileManagerActionsService.FILEMANAGER_UPLOAD_FILE_ERROR),
  //    map((action: UploadFilesErrorAction) =>
  //      this.filemanagerNotfication.sendNotification({
  //        type: 'alert',
  //        title: 'File upload',
  //        message: `${action.payload.files[0].name} exists on the server in this directory`
  //      })
  //    )
  //  )
  //);

  public deleteFileSuccess$: Observable<DeleteFileSuccessAction>;

  constructor(public actions$: Actions,
              public fileManagerActions: FileManagerActionsService,
              public filemanagerNotfication: FilemanagerNotifcations,
              public fileManagerApiService: FileManagerApiService) {

    this.deleteFileSuccess$ = this.actions$
      .pipe(
        ofType(FileManagerActionsService.FILEMANAGER_DELETE_FILE_SUCCESS)
      );

    this.actions$
      .pipe(
        ofType(FileManagerActionsService.FILEMANAGER_MOVE_FILES_ERROR)
      )
      .subscribe((action: IFileManagerAction) => {
        this.onMoveFilesError();
      });
  }

  protected deleteFile(file: IFileModel): Observable<boolean> {
    return this.fileManagerApiService.removeFile(file.toJSON());
  }

  protected downloadFile(file: IFileModel): Observable<IOuterFile> {
    return this.fileManagerApiService.downloadFile(file.toJSON());
  }

  protected deleteFilesSelection(files: string[]): Observable<boolean> {
    return this.fileManagerApiService.removeSelectedFiles(files);
  }

  protected loadFiles(folderId: string | null): Observable<IOuterFile[]> {
    return this.fileManagerApiService.loadFiles(folderId);
  }

  protected uploadFile(file: IOuterFile): Observable<IOuterFile> {
    return this.fileManagerApiService.uploadFile(file);
  }

  protected moveFiles(files: IOuterFile[], folder: IOuterNode = null): Observable<IOuterFile[]> {
    return this.fileManagerApiService.moveFile(files, folder);
  }

  protected onDeleteFileError(file: IFileModel): Action {
    this.filemanagerNotfication.sendNotification({
      type: 'error',
      title: 'Delete file',
      message: '[FILEMANAGER] Can not delete file' + file.name
    });
    return null;
  }

  protected onDeleteFilesSelectionError(files: IOuterFile[]): Action {
    this.filemanagerNotfication.sendNotification({
      type: 'error',
      title: 'Delete selected files',
      message: '[FILEMANAGER] Not all files were deleted'
    });
    return null;
  }

  protected onLoadFilesError(folderId: string): Action {
    this.filemanagerNotfication.sendNotification({
      type: 'error',
      title: 'Load files',
      message: '[FILEMANAGER] Can not load files for folder ' + folderId
    });
    return null;
  }

  protected onMoveFilesSuccess(): Action {
    this.filemanagerNotfication.sendNotification({
      type: 'success',
      title: 'Move files',
      message: 'File was successfully moved to folder'
    });
    return null;
  }

  protected onMoveFilesError(): Action {
    this.filemanagerNotfication.sendNotification({
      type: 'error',
      title: 'Move files',
      message: 'File was not successfully moved to new folder'
    });
    return null;
  }
}
